(function () {
  const CFG = window.FF_UPSELL || {};
  const moneyFormat = CFG.moneyFormat || "${{amount}}";
  const DEFAULT_MAX_PRICE_CENTS = 3500000; // $35.000 COP
  const DEFAULT_COLLECTION_HANDLE = "all";
  const MAX_COLLECTION_PAGES = 4; // 4 x 250 = hasta 1000 productos revisados

  const productPoolCache = {};

  // -------------------------
  // Utils
  // -------------------------
  function formatMoney(cents) {
    const safeCents = Number.isFinite(Number(cents)) ? Number(cents) : 0;

    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      return window.Shopify.formatMoney(safeCents, moneyFormat);
    }

    try {
      return new Intl.NumberFormat("es-CO", {
        style: "currency",
        currency: "COP",
        maximumFractionDigits: 0
      }).format(safeCents / 100);
    } catch {
      return "$" + Math.round(safeCents / 100).toLocaleString("es-CO");
    }
  }

  async function fetchJSON(url, options) {
    const res = await fetch(url, Object.assign({ credentials: "same-origin" }, options || {}));
    if (!res.ok) throw new Error("HTTP " + res.status);
    return res.json();
  }

  function escapeHTML(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  function uniqByHandle(list) {
    const seen = new Set();
    return (list || []).filter(product => {
      if (!product || !product.handle) return false;
      if (seen.has(product.handle)) return false;
      seen.add(product.handle);
      return true;
    });
  }

  function shuffle(arr) {
    const a = (arr || []).slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }

  function normalizePath(path) {
    return String(path || "").replace(/\/+$/, "") || "/";
  }

  function parsePriceToCents(value) {
    if (value === null || typeof value === "undefined" || value === "") return null;

    if (typeof value === "number") {
      return Math.round(value);
    }

    let raw = String(value).trim();
    if (!raw) return null;

    raw = raw.replace(/[^0-9.,-]/g, "");

    if (raw.includes(",") && raw.includes(".")) {
      raw = raw.replace(/\./g, "").replace(",", ".");
    } else if (raw.includes(",") && !raw.includes(".")) {
      raw = raw.replace(",", ".");
    }

    const number = Number(raw);
    if (!Number.isFinite(number)) return null;

    // Algunas respuestas de Shopify devuelven precios en centavos (1200000),
    // otras pueden devolverlos como pesos con decimales (12000.00).
    if (raw.includes(".")) return Math.round(number * 100);
    if (number <= 100000) return Math.round(number * 100);
    return Math.round(number);
  }

  function getVariantPriceCents(variant) {
    if (!variant) return null;
    return parsePriceToCents(
      variant.price ??
      variant.price_cents ??
      variant.price_in_cents ??
      variant.compare_at_price
    );
  }

  function getProductPrices(product) {
    const variants = Array.isArray(product && product.variants) ? product.variants : [];
    const prices = variants
      .map(getVariantPriceCents)
      .filter(price => Number.isFinite(price));

    const productPrice = parsePriceToCents(product && product.price);
    if (Number.isFinite(productPrice)) prices.push(productPrice);

    return prices.filter(price => price > 0);
  }

  function getMinProductPrice(product) {
    const prices = getProductPrices(product);
    if (!prices.length) return null;
    return Math.min.apply(null, prices);
  }

  function getSelectedVariant(product, maxPriceCents) {
    const variants = Array.isArray(product && product.variants) ? product.variants : [];

    const eligibleAvailable = variants.find(variant => {
      const price = getVariantPriceCents(variant);
      const isAvailable = variant.available !== false;
      return isAvailable && Number.isFinite(price) && price <= maxPriceCents;
    });

    if (eligibleAvailable) return eligibleAvailable;

    const anyEligible = variants.find(variant => {
      const price = getVariantPriceCents(variant);
      return Number.isFinite(price) && price <= maxPriceCents;
    });

    return anyEligible || variants[0] || null;
  }

  function productHasDifferentPrices(product) {
    const prices = getProductPrices(product);
    return new Set(prices).size > 1;
  }

  function getProductId(product) {
    if (!product) return "";
    return String(product.id || product.product_id || "");
  }

  function getImageUrl(product) {
    if (!product) return "";

    if (typeof product.featured_image === "string") return product.featured_image;
    if (product.featured_image && typeof product.featured_image === "object") {
      return product.featured_image.url || product.featured_image.src || "";
    }

    if (product.image && typeof product.image === "object") {
      return product.image.src || product.image.url || "";
    }
    if (typeof product.image === "string") return product.image;

    const img0 = (product.images && product.images[0]) ? product.images[0] : "";
    if (typeof img0 === "string") return img0;
    if (img0 && typeof img0 === "object") return img0.src || img0.url || "";

    return "";
  }

  function getProductUrl(product) {
    if (product && product.url) return product.url;
    if (product && product.handle) return "/products/" + product.handle;
    return "#";
  }

  function getMaxPriceFromBlock(block) {
    const raw = block && (block.dataset.maxPriceCents || block.dataset.maxPrice || block.dataset.priceCap);
    const parsed = parsePriceToCents(raw);
    return Number.isFinite(parsed) ? parsed : DEFAULT_MAX_PRICE_CENTS;
  }

  function getSourceCollectionsFromBlock(block) {
    const raw = block && (
      block.dataset.sourceCollections ||
      block.dataset.sourceCollection ||
      block.dataset.collectionHandle
    );

    const handles = String(raw || DEFAULT_COLLECTION_HANDLE)
      .split(",")
      .map(handle => handle.trim())
      .filter(Boolean);

    return handles.length ? handles : [DEFAULT_COLLECTION_HANDLE];
  }

  function getExcludeSet(block) {
    const exclude = new Set();

    if (block && block.dataset.excludeProductId) {
      exclude.add(String(block.dataset.excludeProductId));
    }

    if (block && block.dataset.cartProductIds) {
      block.dataset.cartProductIds
        .split(",")
        .map(value => value.trim())
        .filter(Boolean)
        .forEach(id => exclude.add(String(id)));
    }

    return exclude;
  }

  function isEligibleProduct(product, maxPriceCents, exclude) {
    if (!product || !product.handle) return false;
    if (exclude && exclude.has(getProductId(product))) return false;

    const variant = getSelectedVariant(product, maxPriceCents);
    if (!variant || !variant.id) return false;

    const price = getVariantPriceCents(variant);
    if (!Number.isFinite(price) || price <= 0) return false;

    return price <= maxPriceCents;
  }

  function cardHTML(product, maxPriceCents) {
    const variant = getSelectedVariant(product, maxPriceCents);
    const variantId = variant && variant.id ? String(variant.id) : "";
    const price = getVariantPriceCents(variant) || getMinProductPrice(product) || 0;
    const priceText = productHasDifferentPrices(product)
      ? "A partir de " + formatMoney(price)
      : formatMoney(price);

    const img = getImageUrl(product);
    const url = getProductUrl(product);
    const title = product.title || product.handle || "Producto";

    return `
      <div class="ff-upsell-card" data-product-id="${escapeHTML(getProductId(product))}" data-product-handle="${escapeHTML(product.handle)}">
        <div class="ff-upsell-card__inner">
          <a class="ff-upsell-card__media" href="${escapeHTML(url)}">
            ${img ? `<img loading="lazy" src="${escapeHTML(img)}" alt="${escapeHTML(title)}">` : ``}
          </a>

          <div class="ff-upsell-card__content">
            <p class="ff-upsell-card__title">
              <a href="${escapeHTML(url)}">${escapeHTML(title)}</a>
            </p>

            <div class="ff-upsell-card__price">${escapeHTML(priceText)}</div>

            <div class="ff-upsell-card__actions">
              <button class="ff-upsell-card__btn" data-ff-add ${variantId ? `data-variant-id="${escapeHTML(variantId)}"` : "disabled"}>
                ${variantId ? "Agregar" : "No disponible"}
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  function extractHandleFromCard(card) {
    if (!card) return null;
    if (card.dataset.productHandle) return card.dataset.productHandle;

    const a = card.querySelector('a[href*="/products/"]');
    if (!a) return null;

    const href = a.getAttribute("href") || "";
    try {
      const u = new URL(href, location.origin);
      const parts = u.pathname.split("/").filter(Boolean);
      const idx = parts.indexOf("products");
      if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
    } catch {
      const match = href.match(/\/products\/([^/?#]+)/);
      if (match && match[1]) return match[1];
    }

    return null;
  }

  // -------------------------
  // Product pool: productos <= $35.000 COP según colecciones recomendadas
  // -------------------------
  async function fetchCollectionPage(handle, page) {
    const encodedHandle = encodeURIComponent(handle || DEFAULT_COLLECTION_HANDLE);
    const url = `/collections/${encodedHandle}/products.json?limit=250&page=${page}`;
    const data = await fetchJSON(url);
    return Array.isArray(data.products) ? data.products : [];
  }

  async function fetchProductPool(collectionHandle, maxPriceCents) {
    const handle = collectionHandle || DEFAULT_COLLECTION_HANDLE;
    const cacheKey = `${handle}:${maxPriceCents}`;

    if (productPoolCache[cacheKey]) return productPoolCache[cacheKey];

    const allProducts = [];

    try {
      for (let page = 1; page <= MAX_COLLECTION_PAGES; page++) {
        const pageProducts = await fetchCollectionPage(handle, page);
        allProducts.push.apply(allProducts, pageProducts);
        if (pageProducts.length < 250) break;
      }
    } catch {
      // Fallback por si /collections/all/products.json no responde en algún theme.
      try {
        const data = await fetchJSON(`/products.json?limit=250`);
        allProducts.push.apply(allProducts, Array.isArray(data.products) ? data.products : []);
      } catch {}
    }

    const pool = uniqByHandle(allProducts).filter(product => isEligibleProduct(product, maxPriceCents, new Set()));
    productPoolCache[cacheKey] = pool;
    return pool;
  }

  async function getUpsellProducts(block) {
    const maxPriceCents = getMaxPriceFromBlock(block);
    const sourceCollections = getSourceCollectionsFromBlock(block);
    const exclude = getExcludeSet(block);
    const limit = Number(block.dataset.limit || 3);

    const pools = await Promise.all(
      sourceCollections.map(handle => fetchProductPool(handle, maxPriceCents))
    );

    const pool = uniqByHandle(pools.flat());
    const eligible = pool.filter(product => isEligibleProduct(product, maxPriceCents, exclude));

    return shuffle(eligible).slice(0, limit);
  }

  // -------------------------
  // Cart add + UI refresh (Dawn)
  // -------------------------
  function getSectionsToRequest() {
    const sections = [];

    const addSection = (id, section, selector, type) => {
      if (!section) return;
      if (sections.some(item => item.section === section)) return;
      sections.push({ id, section, selector, type });
    };

    addSection("cart-drawer", "cart-drawer", null, "cart-drawer");
    addSection("cart-icon-bubble", "cart-icon-bubble", ".shopify-section", "generic");

    const mainCartItems = document.getElementById("main-cart-items");
    if (mainCartItems && mainCartItems.dataset.id) {
      addSection("main-cart-items", mainCartItems.dataset.id, ".js-contents", "generic");
    }

    const mainCartFooter = document.getElementById("main-cart-footer");
    if (mainCartFooter && mainCartFooter.dataset.id) {
      addSection("main-cart-footer", mainCartFooter.dataset.id, ".js-contents", "generic");
    }

    return sections;
  }

  function getSectionInnerHTML(html, selector) {
    const parsed = new DOMParser().parseFromString(html || "", "text/html");
    const target = selector ? parsed.querySelector(selector) : null;
    return target ? target.innerHTML : parsed.body.innerHTML;
  }

  function replaceCartDrawer(html) {
    if (!html) return;

    const doc = new DOMParser().parseFromString(html, "text/html");
    const currentDrawerEl = document.querySelector("cart-drawer");
    const freshDrawerEl = doc.querySelector("cart-drawer");

    if (currentDrawerEl && freshDrawerEl) {
      const currentInner = currentDrawerEl.querySelector("#CartDrawer");
      const freshInner = freshDrawerEl.querySelector("#CartDrawer");

      if (currentInner && freshInner) {
        currentInner.innerHTML = freshInner.innerHTML;
      } else {
        currentDrawerEl.innerHTML = freshDrawerEl.innerHTML;
      }
      return;
    }

    const currentSection = document.querySelector("#shopify-section-cart-drawer");
    const freshSection = doc.querySelector("#shopify-section-cart-drawer");
    if (currentSection && freshSection) currentSection.innerHTML = freshSection.innerHTML;
  }

  function replaceGenericSection(id, html, selector) {
    if (!id || !html) return;

    const container = document.getElementById(id);
    if (!container) return;

    const elementToReplace = selector ? (container.querySelector(selector) || container) : container;
    elementToReplace.innerHTML = getSectionInnerHTML(html, selector);
  }

  async function addToCart(variantId) {
    const sectionsToRequest = getSectionsToRequest();
    const body = {
      id: Number(variantId),
      quantity: 1,
      sections: sectionsToRequest.map(item => item.section),
      sections_url: window.location.pathname
    };

    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    });

    if (!res.ok) throw new Error("No se pudo agregar");

    const data = await res.json();
    data._ffSectionsToRequest = sectionsToRequest;
    return data;
  }

  async function refreshDawnCartUI(addResponse, sourceContext) {
    try {
      const sectionsToRequest = (addResponse && addResponse._ffSectionsToRequest) || getSectionsToRequest();
      let sectionsHTML = addResponse && addResponse.sections ? addResponse.sections : null;

      if (!sectionsHTML) {
        const endpoint = normalizePath(window.location.pathname) === "/cart" ? "/cart" : "/";
        const res = await fetch(`${endpoint}?sections=${sectionsToRequest.map(item => item.section).join(",")}`, {
          credentials: "same-origin"
        });
        if (!res.ok) return false;
        sectionsHTML = await res.json();
      }

      sectionsToRequest.forEach(sectionInfo => {
        const html = sectionsHTML[sectionInfo.section];
        if (!html) return;

        if (sectionInfo.type === "cart-drawer") {
          replaceCartDrawer(html);
        } else {
          replaceGenericSection(sectionInfo.id, html, sectionInfo.selector);
        }
      });

      const shouldOpenDrawer = sourceContext !== "cart";
      const drawer = document.querySelector("cart-drawer");
      if (shouldOpenDrawer && drawer && typeof drawer.open === "function") drawer.open();

      watchCartDrawer();
      scheduleInitAll(true);

      return true;
    } catch {
      return false;
    }
  }

  // -------------------------
  // Binding add buttons
  // -------------------------
  function bindAddButtons(root) {
    root.querySelectorAll("[data-ff-add]").forEach(button => {
      if (button.dataset.ffBound === "1") return;
      button.dataset.ffBound = "1";

      button.addEventListener("click", async () => {
        const variantId = button.getAttribute("data-variant-id");
        if (!variantId) return;

        const previousText = button.textContent;
        const card = button.closest(".ff-upsell-card");
        const block = button.closest("[data-ff-upsell]");
        const sourceContext = block ? (block.dataset.context || "unknown") : "unknown";
        const handle = extractHandleFromCard(card);

        button.disabled = true;
        button.textContent = "Agregando...";

        try {
          const addResponse = await addToCart(variantId);

          if (card) card.remove();

          // Evita que el mismo producto reaparezca en ese bloque durante esta vista.
          if (block && handle) {
            const currentHidden = block.dataset.ffAddedHandles || "";
            const added = currentHidden.split(",").map(item => item.trim()).filter(Boolean);
            if (!added.includes(handle)) added.push(handle);
            block.dataset.ffAddedHandles = added.join(",");
          }

          const refreshed = await refreshDawnCartUI(addResponse, sourceContext);
          if (!refreshed && normalizePath(window.location.pathname) === "/cart") {
            window.location.reload();
            return;
          }

          button.textContent = "Agregado ✓";
        } catch {
          button.textContent = "Error";
          setTimeout(() => {
            button.textContent = previousText;
            button.disabled = false;
          }, 900);
        }
      });
    });
  }

  // -------------------------
  // Init blocks
  // -------------------------
  async function initBlock(block, force) {
    const grid = block.querySelector("[data-ff-upsell-grid]");
    if (!grid) return;

    if (!force && grid.children.length && block.dataset.ffHydrated === "1") {
      bindAddButtons(block);
      return;
    }

    const maxPriceCents = getMaxPriceFromBlock(block);
    let products = await getUpsellProducts(block);

    const addedHandles = (block.dataset.ffAddedHandles || "")
      .split(",")
      .map(item => item.trim())
      .filter(Boolean);

    if (addedHandles.length) {
      products = products.filter(product => !addedHandles.includes(product.handle));
    }

    if (!products.length) {
      grid.innerHTML = "";
      block.dataset.ffHydrated = "1";
      return;
    }

    grid.innerHTML = products.map(product => cardHTML(product, maxPriceCents)).join("");
    bindAddButtons(block);
    block.dataset.ffHydrated = "1";
  }

  // -------------------------
  // Init all (debounced + guard)
  // -------------------------
  let initRunning = false;
  let initTimer = null;

  function scheduleInitAll(force) {
    if (initTimer) clearTimeout(initTimer);
    initTimer = setTimeout(() => {
      initAll(Boolean(force));
    }, 80);
  }

  async function initAll(force) {
    if (initRunning) return;
    initRunning = true;

    try {
      const blocks = Array.from(document.querySelectorAll("[data-ff-upsell]"));
      for (const block of blocks) {
        await initBlock(block, force);
      }
    } finally {
      initRunning = false;
    }
  }

  // -------------------------
  // Observer: si Dawn actualiza el drawer, re-hidratamos upsell.
  // -------------------------
  let drawerObserver = null;

  function watchCartDrawer() {
    try {
      if (drawerObserver) drawerObserver.disconnect();

      const drawer = document.querySelector("cart-drawer");
      if (!drawer) return;

      const target = drawer.querySelector("#CartDrawer") || drawer;

      drawerObserver = new MutationObserver(() => {
        if (initRunning) return;

        const anyEmpty = Array.from(document.querySelectorAll('[data-ff-upsell][data-context="cart-drawer"] [data-ff-upsell-grid]'))
          .some(grid => grid && grid.children.length === 0);

        if (anyEmpty) scheduleInitAll(true);
      });

      drawerObserver.observe(target, { childList: true, subtree: true });
    } catch {}
  }

  document.addEventListener("DOMContentLoaded", () => {
    initAll(false);
    watchCartDrawer();
  });

  document.addEventListener("shopify:section:load", () => {
    scheduleInitAll(true);
  });
})();
